import React from 'react';
import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { LIST_VOUCHERS_RESOURCE } from '../../api/useListVouchers';
import { VOUCHER_RESOURCE } from 'src/features/vouchers/api/useGetVoucher';
import { useUpdateVoucher } from 'src/features/vouchers/api/useUpdateVoucher';
import { Voucher } from 'src/features/vouchers/utils/types';
import VoucherFormModal from 'src/features/vouchers/components/forms/VoucherFormModal';
import { vouchersMessages } from 'src/features/vouchers/utils/messages';

interface UpdateVoucherFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedVoucher: Voucher;
    children?: React.ReactNode;
}

function UpdateVoucherFormModal<TData>({
    selectedVoucher,
    className,
    semantics,
    children,
}: UpdateVoucherFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateVoucher({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_VOUCHERS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [VOUCHER_RESOURCE] });
            toast({
                title: formatMessage(vouchersMessages.updateVoucherSuccess),
                description: formatMessage(vouchersMessages.updateVoucherSuccessDescription),
            });
        },
    });

    return (
        <VoucherFormModal
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ voucherId: selectedVoucher.id, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(vouchersMessages.editVoucherSubtitle)}
            selectedVoucher={selectedVoucher}
        >
            {children}
        </VoucherFormModal>
    );
}

export default UpdateVoucherFormModal;
